import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Edit as EditIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import api from '../utils/api';

interface DocumentCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  color: string;
  is_active: boolean;
  document_count: number;
}

interface Document {
  id: number;
  title: string;
  description: string;
  category: DocumentCategory | null;
  file: string;
  file_size: number;
  file_size_display: string;
  version: string;
  is_current_version: boolean;
  visibility: string;
  requires_acknowledgment: boolean;
  revision_date: string;
  effective_date: string | null;
  expiration_date: string | null;
  is_active: boolean;
  created_by: any;
  created_at: string;
  acknowledgment_summary: any;
  requires_revision_for_edits?: boolean;
}

interface DocumentEditFormData {
  title: string;
  description: string;
  category_id: number;
  visibility: string;
  requires_acknowledgment: boolean;
  effective_date: string;
  expiration_date: string;
}

interface DocumentEditDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
  categories: DocumentCategory[];
  onDocumentUpdated?: () => void;
  onRequiresRevision?: () => void;
}

const DocumentEditDialog: React.FC<DocumentEditDialogProps> = ({
  open,
  onClose,
  document,
  categories,
  onDocumentUpdated,
  onRequiresRevision
}) => {
  const [form, setForm] = useState<DocumentEditFormData>({
    title: '',
    description: '',
    category_id: 0,
    visibility: 'assigned',
    requires_acknowledgment: true,
    effective_date: '',
    expiration_date: ''
  });
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const requiresRevision = document?.requires_revision_for_edits === true;

  useEffect(() => {
    if (document) {
      setForm({
        title: document.title,
        description: document.description,
        category_id: document.category?.id || 0,
        visibility: document.visibility,
        requires_acknowledgment: document.requires_acknowledgment,
        effective_date: document.effective_date || '',
        expiration_date: document.expiration_date || ''
      });
      setSelectedFile(null);
      setError(null);
      setSuccess(null);
    }
  }, [document]);

  const handleSubmit = async () => {
    if (!document) return;

    if (requiresRevision) {
      onRequiresRevision?.();
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!form.title.trim()) {
        setError('Document title is required');
        return;
      }

      if (!form.category_id) {
        setError('Please select a category');
        return;
      }

      if (selectedFile) {
        const formData = new FormData();
        formData.append('title', form.title.trim());
        formData.append('description', form.description.trim());
        formData.append('category_id', String(form.category_id));
        formData.append('visibility', form.visibility);
        formData.append('requires_acknowledgment', String(form.requires_acknowledgment));
        if (form.effective_date) {
          formData.append('effective_date', form.effective_date);
        }
        if (form.expiration_date) {
          formData.append('expiration_date', form.expiration_date);
        }
        formData.append('file', selectedFile);
        await api.patch(`/documents/${document.id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        const updateData = {
          title: form.title.trim(),
          description: form.description.trim(),
          category_id: form.category_id,
          visibility: form.visibility,
          requires_acknowledgment: form.requires_acknowledgment,
          effective_date: form.effective_date || null,
          expiration_date: form.expiration_date || null
        };
        await api.patch(`/documents/${document.id}/`, updateData);
      }
      
      setSuccess('Document updated successfully');
      onDocumentUpdated?.();
      
      setTimeout(() => {
        onClose();
      }, 500);

    } catch (error: any) {
      console.error('Error updating document:', error);
      if (error.response?.data?.requires_revision) {
        setError(
          error.response.data.error ||
          'This document has completed assignments. Create a new revision to make changes.'
        );
        onRequiresRevision?.();
        return;
      }
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errorMessages = Object.entries(errorData)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          setError(`Update failed:\n${errorMessages}`);
        } else {
          setError(`Update failed: ${errorData}`);
        }
      } else {
        setError('Failed to update document. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!document) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <EditIcon sx={{ mr: 1 }} />
          Edit Document: {document.title}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {requiresRevision && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This document has completed assignments. Use <strong>Create Revision</strong> (version history)
            to change the file or metadata. Direct edits are locked to preserve compliance records.
          </Alert>
        )}

        {!requiresRevision && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You can edit this document freely until someone completes an assignment for it.
            After that, changes require creating a new revision.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Document Title"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              required
              disabled={requiresRevision}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              disabled={requiresRevision}
            />
          </Grid>

          {!requiresRevision && (
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' }
                }}
                onClick={() => {
                  const fileInput = window.document.getElementById('edit-document-file-input');
                  if (fileInput) fileInput.click();
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="subtitle1" gutterBottom>
                  {selectedFile ? selectedFile.name : 'Replace PDF file (optional)'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Leave unchanged to keep the current file. PDF only, max 25MB.
                </Typography>
                <input
                  id="edit-document-file-input"
                  type="file"
                  accept=".pdf,application/pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    if (file && file.type !== 'application/pdf') {
                      setError('Please select a PDF file');
                      return;
                    }
                    setSelectedFile(file);
                    setError(null);
                  }}
                />
              </Box>
            </Grid>
          )}
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required disabled={requiresRevision}>
              <InputLabel>Category</InputLabel>
              <Select
                value={form.category_id || ''}
                label="Category"
                onChange={(e) => setForm(prev => ({ ...prev, category_id: e.target.value as number }))}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={requiresRevision}>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={form.visibility}
                label="Visibility"
                onChange={(e) => setForm(prev => ({ ...prev, visibility: e.target.value }))}
              >
                <MenuItem value="assigned">Assignment Only</MenuItem>
                <MenuItem value="public">Public - All Users</MenuItem>
                <MenuItem value="department">Department Restricted</MenuItem>
                <MenuItem value="role">Role Restricted</MenuItem>
                <MenuItem value="location">Location Restricted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.requires_acknowledgment}
                  onChange={(e) => setForm(prev => ({ ...prev, requires_acknowledgment: e.target.checked }))}
                  disabled={requiresRevision}
                />
              }
              label="Requires Acknowledgment"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Effective Date"
              type="date"
              value={form.effective_date}
              onChange={(e) => setForm(prev => ({ ...prev, effective_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              disabled={requiresRevision}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Expiration Date"
              type="date"
              value={form.expiration_date}
              onChange={(e) => setForm(prev => ({ ...prev, expiration_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              disabled={requiresRevision}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Document Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Version:</strong> {document.version} {document.is_current_version && '(Current)'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>File Size:</strong> {document.file_size_display}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Created:</strong> {new Date(document.created_at).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Created By:</strong> {document.created_by ? `${document.created_by.first_name} ${document.created_by.last_name}` : 'Unknown'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {requiresRevision ? 'Close' : 'Cancel'}
        </Button>
        {requiresRevision ? (
          <Button
            onClick={() => onRequiresRevision?.()}
            variant="contained"
            color="warning"
          >
            Create Revision
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {loading ? 'Updating...' : 'Update Document'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentEditDialog;
